import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { autoResponsesAPI } from "../services/api";
import { useToast } from "../hooks/use-toast";
import { Plus, Trash2, Edit2, MessageSquareDashed, Zap } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const QuickReplies = () => {
  const [responses, setResponses] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newReply, setNewReply] = useState({ trigger: "", response: "" });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingReply, setEditingReply] = useState({
    id: "",
    trigger: "",
    response: "",
  });

  const { toast } = useToast();

  useEffect(() => {
    loadResponses();
  }, []);

  const loadResponses = async () => {
    try {
      const res = await autoResponsesAPI.getAll();
      setResponses(res.data);
    } catch (error) {
      console.error("Erro ao carregar respostas:", error);
    }
  };

  const handleAdd = async () => {
    if (!newReply.trigger || !newReply.response) return;

    const triggerFormatado = newReply.trigger.startsWith("/")
      ? newReply.trigger
      : `/${newReply.trigger}`;

    try {
      await autoResponsesAPI.create({
        trigger: triggerFormatado,
        response: newReply.response,
      });
      toast({ title: "Sucesso", description: "Resposta rápida adicionada!" });
      setShowAddDialog(false);
      setNewReply({ trigger: "", response: "" });
      loadResponses();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao salvar.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async () => {
    if (!editingReply.trigger || !editingReply.response) return;

    const triggerFormatado = editingReply.trigger.startsWith("/")
      ? editingReply.trigger
      : `/${editingReply.trigger}`;

    try {
      await autoResponsesAPI.update(editingReply.id, {
        trigger: triggerFormatado,
        response: editingReply.response,
      });
      toast({
        title: "Sucesso",
        description: "Atalho atualizado com sucesso!",
      });
      setShowEditDialog(false);
      loadResponses();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao atualizar.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await autoResponsesAPI.delete(id);
      setResponses(responses.filter((r) => r.id !== id));
      toast({
        title: "Atalho apagado",
        description: "Atalho removido com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error.response?.data?.detail || "Erro ao remover atendente.",
        variant: "destructive",
      });
    }
  };

  const openEditModal = (resp) => {
    setEditingReply({
      id: resp.id,
      trigger: resp.trigger,
      response: resp.response,
    });
    setShowEditDialog(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex gap-2">
            <Zap className="w-8 h-8 text-yellow-300 shrink-0" />
            Minhas Respostas Rápidas
          </h1>
          <p className="text-gray-500 mt-1">
            Crie atalhos para digitar mais rápido no chat
          </p>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="w-4/5 sm:w-auto self-center gap-2 shadow-sm">
              <Plus className="w-4 h-4 mr-2" />
              Novo Atalho
            </Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl sm:text-2xl">Criar Resposta Rápida</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2 w-full">
              <div className="space-y-2">
                <Label className="font-bold text-gray-700">Comando de Atalho</Label>
                <Input
                  placeholder="Ex: /dia"
                  value={newReply.trigger}
                  onChange={(e) =>
                    setNewReply({ ...newReply, trigger: e.target.value })
                  }
                  className="bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold text-gray-700">Mensagem Completa</Label>
                <textarea
                  className="w-full min-h-[120px] p-3 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                  placeholder="Ex: Bom dia! Tudo bem? Como posso te ajudar hoje?"
                  value={newReply.response}
                  onChange={(e) =>
                    setNewReply({ ...newReply, response: e.target.value })
                  }
                />
              </div>
              
              <div className="pt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddDialog(false)}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAdd} 
                  className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 shadow-md"
                >
                  Salvar Atalho
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {responses.map((resp) => (
              <div
                key={resp.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
              >
                <div className="flex-1 pr-4">
                  <p className="font-bold text-blue-600 mb-1">{resp.trigger}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {resp.response}
                  </p>
                </div>

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(resp)}
                  >
                    <Edit2 className="w-4 h-4 text-blue-600" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="w-full max-w-[95vw] sm:max-w-md rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Tem certeza disso?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação não pode ser desfeita. Isso excluirá
                          permanentemente o atalho do nosso banco de dados.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
                        <AlertDialogCancel className="sm:mt-0">
                          Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(resp.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Sim, excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}

            {responses.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <MessageSquareDashed className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>Nenhum atalho cadastrado ainda.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">Editar Resposta Rápida</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 w-full">
            <div className="space-y-2">
              <Label className="font-bold text-gray-700">Comando de Atalho</Label>
              <Input
                placeholder="Ex: /dia"
                value={editingReply.trigger}
                onChange={(e) =>
                  setEditingReply({ ...editingReply, trigger: e.target.value })
                }
                className="bg-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-bold text-gray-700">Mensagem Completa</Label>
              <textarea
                className="w-full min-h-[120px] p-3 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                placeholder="Sua mensagem aqui..."
                value={editingReply.response}
                onChange={(e) =>
                  setEditingReply({ ...editingReply, response: e.target.value })
                }
              />
            </div>
            
            <div className="pt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
              <Button 
                variant="outline" 
                onClick={() => setShowEditDialog(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEdit} 
                className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 shadow-md"
              >
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickReplies;
